import React, { useEffect, useMemo, useState } from "react";
import { Modal, Descriptions, App } from "antd";
import { getEffectivePolicyholder, sendOrderConfirmation, IdType, type OrderEffectivePolicyholderVO } from "@ifa/utils";
import copyIcon from "../assets/svg/copy.svg";
import CustomButton from "./CustomButton";

interface SendCorrectionModalProps {
  open: boolean;
  onCancel: () => void;
  orderId: number;
}

export const SendCorrectionModal: React.FC<SendCorrectionModalProps> = ({ open, onCancel, orderId }) => {
  const { message } = App.useApp();
  const [copyLoading, setCopyLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);
  const [correctionLink, setCorrectionLink] = useState("");
  const [policyholder, setPolicyholder] = useState<OrderEffectivePolicyholderVO | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setCustomerLoading(true);
    setLinkLoading(true);
    setCorrectionLink("");

    getEffectivePolicyholder(orderId)
      .then((data) => {
        if (!cancelled) {
          setPolicyholder(data ?? null);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch effective policyholder:", error);
        if (!cancelled) {
          setPolicyholder(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCustomerLoading(false);
        }
      });

    sendOrderConfirmation(orderId, false)
      .then((link) => {
        if (!cancelled) {
          setCorrectionLink(link ?? "");
        }
      })
      .catch((error) => {
        console.error("Failed to prepare correction link:", error);
        if (!cancelled) {
          setCorrectionLink("");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLinkLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, orderId]);

  const customerName = useMemo(() => {
    if (policyholder?.chineseName?.trim()) {
      return policyholder.chineseName.trim();
    }

    if (policyholder?.englishName?.trim()) {
      return policyholder.englishName.trim();
    }

    return "-";
  }, [policyholder]);

  const email = policyholder?.email?.trim() || "-";
  const phone = policyholder?.phone?.trim();
  const normalizedPhone = phone?.replace(/\s+/g, "") || "";
  const isMainland = policyholder?.idType === IdType.IDCard || normalizedPhone.startsWith("+86") || normalizedPhone.startsWith("86");

  const handleCopyLink = async () => {
    if (linkLoading) {
      message.info("連結生成中，請稍候再試");
      return;
    }

    if (!correctionLink) {
      message.error("連結尚未就緒，請稍後重試");
      return;
    }

    try {
      setCopyLoading(true);
      const success = await copyText(correctionLink);
      if (success) {
        message.success("連結已複製");
      } else {
        message.error("複製失敗，請稍後重試");
      }
    } catch (error) {
      console.error("handleCopyLink error:", error);
      message.error("複製失敗，請稍後重試");
    } finally {
      setCopyLoading(false);
    }
  };

  const copyText = async (text: string): Promise<boolean> => {
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.error("Clipboard API failed:", err);
      }
    }

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    return execCommandCopy(text, isIOS);
  };

  const execCommandCopy = (text: string, isIOS: boolean): boolean => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    textarea.style.top = (window.pageYOffset || document.documentElement.scrollTop) + "px";
    textarea.style.fontSize = "12pt";

    document.body.appendChild(textarea);

    if (isIOS) {
      const range = document.createRange();
      range.selectNodeContents(textarea);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      textarea.setSelectionRange(0, textarea.value.length);
    } else {
      textarea.focus();
      textarea.select();
    }

    let success = false;
    try {
      success = document.execCommand("copy");
    } catch (err) {
      console.error("execCommand failed:", err);
    } finally {
      document.body.removeChild(textarea);
      if (window.getSelection) {
        window.getSelection()?.removeAllRanges();
      }
    }

    return success;
  };

  const handleSend = async () => {
    try {
      setSendLoading(true);
      await sendOrderConfirmation(orderId, true);
      message.success("發送成功");
      onCancel();
    } catch (error) {
      console.error(error);
    } finally {
      setSendLoading(false);
    }
  };

  return (
    <Modal title="發送批改檔案" open={open} onCancel={onCancel} footer={null} width={400} centered>
      <div className="flex flex-col gap-4">
        <Descriptions column={1} colon={false} styles={{ content: { justifyContent: "flex-end" } }}>
          <Descriptions.Item label="發送客戶">{customerLoading ? "載入中..." : customerName}</Descriptions.Item>
          {phone && <Descriptions.Item label="手提號碼">{phone}</Descriptions.Item>}
          <Descriptions.Item label="電郵">{customerLoading ? "載入中..." : email}</Descriptions.Item>
        </Descriptions>

        <div className="flex gap-4 mt-2">
          <CustomButton block onClick={handleCopyLink} icon={<img src={copyIcon} alt="copy" style={{ width: 14, height: 14 }} />} loading={copyLoading || linkLoading}>
            複製連結
          </CustomButton>
          <CustomButton block type="primary" onClick={handleSend} loading={sendLoading}>
            確認發送
          </CustomButton>
        </div>

        <div className="bg-red-50 p-3 rounded text-xs text-red-500 mt-2">
          {isMainland ? (
            <>批改檔案將透過電郵發送（大陸客戶無法發送保險相關內容短訊），您也可複製連結透過即時通訊工具發送給客戶。</>
          ) : (
            <>批改檔案將透過短訊和電郵同步發送，您也可複製連結透過即時通訊工具發送給客戶。</>
          )}
        </div>
      </div>
    </Modal>
  );
};
